import { useState, useEffect } from "react";
import { API, useAuth , authFetch } from "@/App";
import { toast } from "sonner";
import { CalendarDays, CheckCircle, XCircle, Clock, Plus, Pause, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ApprovalActionDialog, canApprove } from "@/components/ApprovalActionDialog";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

const LEAVE_TYPES = ["CASUAL", "SICK", "PAID", "UNPAID"];
const STATUS_COLORS = { PENDING: "bg-amber-100 text-amber-700", APPROVED: "bg-emerald-100 text-emerald-700", REJECTED: "bg-red-100 text-red-700", ON_HOLD: "bg-orange-100 text-orange-700" };

const LeavePage = () => {
  const { user } = useAuth();
  const canApproveLeaves = canApprove(user);
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(null); // {entityId, entityLabel, action}
  const [form, setForm] = useState({ employee_id: "", leave_type: "CASUAL", from_date: "", to_date: "", days: 1, reason: "" });

  const fetchData = async ({ silent = false } = {}) => {
    try {
      const [lRes, eRes] = await Promise.all([
        authFetch(`${API}/leaves${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`, {  }),
        authFetch(`${API}/employees?status=ACTIVE`, {  }),
      ]);
      const [lData, eData] = await Promise.all([lRes.json(), eRes.json()]);
      if (lData.success) setLeaves(lData.data);
      if (eData.success) setEmployees(eData.data);
    } catch (err) {
      if (!silent) toast.error("Failed to load leaves");
      console.error(err);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const { lastUpdatedAt, refreshing, manualRefresh } = useAutoRefresh(fetchData, {
    user,
    eventTypes: ["leave"],
    pollIntervalMs: 30000,
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/leaves`, { method: "POST", headers: { "Content-Type": "application/json" },  body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) { toast.success("Leave request created"); setShowCreate(false); setForm({ employee_id: "", leave_type: "CASUAL", from_date: "", to_date: "", days: 1, reason: "" }); fetchData(); }
      else toast.error(data.detail || "Failed");
    } catch { toast.error("Network error"); }
  };

  const handleAction = async (leaveId, action, reason = "") => {
    try {
      const res = await authFetch(`${API}/leaves/${leaveId}/${action}`, { method: "POST", headers: { "Content-Type": "application/json" },  body: JSON.stringify({ reason }) });
      const data = await res.json();
      if (data.success) { toast.success(`Leave ${action}d`); fetchData(); }
      else toast.error(data.detail || "Failed");
    } catch { toast.error("Network error"); }
  };

  // Summary counts
  const pending = leaves.filter(l => l.status === "PENDING").length;
  const approved = leaves.filter(l => l.status === "APPROVED").length;
  const rejected = leaves.filter(l => l.status === "REJECTED").length;

  return (
    <div className="space-y-6" data-testid="leave-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Leave Management</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 flex items-center gap-2">
            Manage employee leave requests
            {lastUpdatedAt && (
              <span className="text-[10px] text-slate-400">
                · Last updated {lastUpdatedAt.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={manualRefresh}
            disabled={refreshing}
            className="h-9"
            data-testid="leave-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
          {/* Add button removed – leave requests are filed from the mobile app */}
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-['Sora']">New Leave Request</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4" data-testid="create-leave-form">
              <div>
                <label className="text-sm font-medium text-slate-700">Employee</label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="leave-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name} ({e.employee_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Leave Type</label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">From</label>
                  <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="mt-1" required data-testid="leave-from-date" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">To</label>
                  <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="mt-1" required data-testid="leave-to-date" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Days</label>
                <Input type="number" min="0.5" step="0.5" value={form.days} onChange={(e) => setForm({ ...form, days: parseFloat(e.target.value) })} className="mt-1" data-testid="leave-days-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Reason</label>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason for leave" className="mt-1" data-testid="leave-reason-input" />
              </div>
              <button type="submit" className="w-full py-2.5 bg-[#8B1A1A] text-white rounded-lg font-medium hover:bg-[#6B1515]" data-testid="submit-leave-btn">Submit Leave Request</button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Pending Approvals Alert */}
      {canApproveLeaves && pending > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="pending-leaves-alert">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">{pending} leave request{pending > 1 ? "s" : ""} awaiting approval</p>
              <p className="text-sm text-amber-600">Review pending leave requests below and approve, reject, or hold.</p>
            </div>
            <button onClick={() => setStatusFilter("PENDING")} className="px-3 py-1.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-100 text-sm" data-testid="review-pending-leaves">Review Now</button>
          </div>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center"><p className="text-2xl font-bold text-amber-600 font-mono">{pending}</p><p className="text-sm text-slate-500">Pending</p></Card>
        <Card className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600 font-mono">{approved}</p><p className="text-sm text-slate-500">Approved</p></Card>
        <Card className="p-4 text-center"><p className="text-2xl font-bold text-red-600 font-mono">{rejected}</p><p className="text-sm text-slate-500">Rejected</p></Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="leave-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leave Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="leaves-table">
            <thead>
              <tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th>{canApproveLeaves && <th>Actions</th>}</tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></td></tr>
              ) : leaves.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-slate-500">No leave requests</td></tr>
              ) : leaves.map((l, idx) => (
                <tr key={l.leave_id || `leave-${idx}`}>
                  <td className="text-sm font-medium">{l.employee_name}</td>
                  <td><Badge variant="outline" className="text-xs">{l.leave_type}</Badge></td>
                  <td className="text-sm">{l.from_date}</td>
                  <td className="text-sm">{l.to_date}</td>
                  <td className="text-sm font-mono">{l.days}</td>
                  <td className="text-sm text-slate-600 max-w-[200px] truncate">{l.reason || "—"}</td>
                  <td><Badge className={`text-xs ${STATUS_COLORS[l.status]}`}>{l.status}</Badge></td>
                  {canApproveLeaves && (
                    <td>
                      {(l.status === "PENDING" || l.status === "ON_HOLD") && (
                        <div className="flex gap-2">
                          <button onClick={() => setApprovalDialog({ entityId: l.leave_id, entityLabel: `${l.employee_name} · ${l.leave_type} · ${l.days}d`, action: "APPROVE" })} className="p-1.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-600" data-testid={`approve-leave-${l.leave_id}`} title="Approve"><CheckCircle className="w-4 h-4" /></button>
                          {l.status === "PENDING" && (
                            <button onClick={() => setApprovalDialog({ entityId: l.leave_id, entityLabel: `${l.employee_name} · ${l.leave_type} · ${l.days}d`, action: "HOLD" })} className="p-1.5 rounded bg-amber-50 hover:bg-amber-100 text-amber-600" data-testid={`hold-leave-${l.leave_id}`} title="Hold"><Pause className="w-4 h-4" /></button>
                          )}
                          <button onClick={() => setApprovalDialog({ entityId: l.leave_id, entityLabel: `${l.employee_name} · ${l.leave_type} · ${l.days}d`, action: "REJECT" })} className="p-1.5 rounded bg-red-50 hover:bg-red-100 text-red-600" data-testid={`reject-leave-${l.leave_id}`} title="Reject"><XCircle className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Unified Approval/Reject/Hold Dialog */}
      {approvalDialog && (
        <ApprovalActionDialog
          open={!!approvalDialog}
          onClose={() => setApprovalDialog(null)}
          entity="leave"
          action={approvalDialog.action}
          entityId={approvalDialog.entityId}
          entityLabel={approvalDialog.entityLabel}
          endpointBase="/leaves"
          onSuccess={() => fetchData()}
        />
      )}
    </div>
  );
};

export default LeavePage;
